/**
 * Dev-only mock layer (VITE_MOCK_AUTH=true). Click through every admin page
 * with no backend — seeded admin, plans catalog, orgs, users, usage, with
 * working mutations held in memory.
 */
import {
  AI_CREDIT_PACKAGES,
  isUnlimited,
  PLAN_LIMITS,
  planAuditRetentionDays,
  planHasAuditLogs,
  toIsoDate,
  type AdminMember,
  type AiCreditPackageRecord,
  type AiCreditPackageWrite,
  type AdminOrgDetail,
  type AdminOrgSummary,
  type AdminUserDetail,
  type AdminUserOrgRef,
  type AdminUserSummary,
  type AiGlobalSettings,
  type BillingSummary,
  type DataStatus,
  type GrantInfo,
  type GrantKind,
  type GrantRequest,
  type InvoiceDTO,
  type InvoiceStatus,
  type NamedSeries,
  type OrgPlan,
  type OrgRole,
  type OrgStatus,
  type Paginated,
  type PlanDetail,
  type PlanInterval,
  type PlanKind,
  type PlanOrgRef,
  type PlanRecord,
  type PlanTier,
  type PlanUsageInfo,
  type PlanWriteRequest,
  type RefundRequest,
  type SetPlanRequest,
  type UsageAnalytics,
  type UsageGranularity,
  type UsagePoint,
  type IncidentDTO,
  type IncidentImpact,
  type IncidentStatus,
  type StatusResponse,
  type UptimeDay,
} from "@currencycore/shared";

export const MOCK_ENABLED = import.meta.env["VITE_MOCK_AUTH"] === "true";
export const MOCK_SESSION_KEY = "cc_admin_mock_session";

export const MOCK_ADMIN = { id: "usr_admin", name: "Platform Admin", email: "admin@currency-core.com" };

const TIER_LABEL: Record<OrgPlan, string> = {
  free: "Free",
  starter: "Starter",
  growth: "Growth",
  growth_plus: "Growth Plus",
  business: "Business",
  enterprise: "Enterprise",
  custom: "Custom",
};

// --- plan catalog -----------------------------------------------------------

function mkPlan(
  id: string,
  name: string,
  kind: PlanKind,
  monthlyRequests: number | null,
  rateLimit: number | null,
  prices: PlanRecord["prices"],
  overageCents: number | null = null,
  overageUnit: number | null = null,
  dodoProductIds: PlanRecord["dodoProductIds"] = {},
): PlanRecord {
  // Feature flags + AI/seat limits mirror PLAN_LIMITS for standard tiers; custom
  // plans are bespoke enterprise deals → all features, unlimited AI/seats.
  const def = id in PLAN_LIMITS ? PLAN_LIMITS[id as PlanTier] : null;
  return {
    id,
    name,
    kind,
    monthlyRequests,
    rateLimit,
    aiCreditLimit: def ? def.aiCreditLimit : null,
    seatLimit: def ? def.seatLimit : null,
    prices,
    overageCents,
    overageUnit,
    mcpEnabled: def ? def.mcpEnabled : true,
    ssoEnabled: def ? def.ssoEnabled : true,
    auditLogsEnabled: def ? def.auditLogsEnabled : true,
    auditRetentionDays: def ? def.auditRetentionDays : 1095,
    prioritySupportEnabled: def ? def.prioritySupportEnabled : true,
    customSlaEnabled: def ? def.customSlaEnabled : true,
    historyApiEnabled: def ? def.historyApiEnabled : true,
    dodoProductIds,
    archived: false,
    createdAt: "2026-01-01T00:00:00Z",
    updatedAt: "2026-01-01T00:00:00Z",
  };
}

const PLANS: PlanRecord[] = [
  mkPlan("free", "Free", "standard", 1_000, 60, { monthly: 0, annual: 0 }),
  mkPlan("starter", "Starter", "standard", 50_000, 120, { monthly: 900, annual: 9000 }, 50, 10000),
  mkPlan("growth", "Growth", "standard", 500_000, 600, { monthly: 2900, annual: 29000 }, 40, 10000),
  mkPlan("growth_plus", "Growth Plus", "standard", 2_000_000, 3000, { monthly: 7900, annual: 79000 }, 25, 10000),
  mkPlan("business", "Business", "standard", 10_000_000, 6000, { monthly: 19900, annual: 199000 }, 15, 10000),
  mkPlan("enterprise", "Enterprise", "standard", null, 12000, {}),
  mkPlan("plan_hooli", "Hooli Enterprise+", "custom", 5_000_000, 8000, { monthly: 49900, quarterly: 134900, annual: 499000 }, 20, 10000),
];

const planById = (id: string) => PLANS.find((p) => p.id === id);

// --- orgs / users -----------------------------------------------------------

interface MockOrg {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  createdAt: string;
  planId: string;
  billingInterval: PlanInterval | null;
  currentUsage: number;
  grantRequests: number | null;
  grantStart: string | null;
  grantKind: GrantKind | null;
  grantEnd: string | null;
  members: { userId: string; role: OrgRole; joinedAt: string }[];
}

interface MockUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  banned: boolean;
  bannedReason: string | null;
  bannedAt: string | null;
  createdAt: string;
}

const USERS: MockUser[] = [
  ["Ada Lovelace", "ada@acme.test"],
  ["Alan Turing", "alan@acme.test"],
  ["Grace Hopper", "grace@globex.test"],
  ["Katherine Johnson", "katherine@globex.test"],
  ["Linus Pauling", "linus@initech.test"],
  ["Marie Curie", "marie@umbrella.test"],
  ["Nikola Tesla", "nikola@umbrella.test"],
  ["Rosalind Franklin", "rosalind@hooli.test"],
  ["Tim Berners-Lee", "tim@hooli.test"],
  ["Dennis Ritchie", "dennis@piedpiper.test"],
  ["Margaret Hamilton", "margaret@piedpiper.test"],
  ["Banned Bob", "bob@spam.test"],
].map(([name, email], i) => ({
  id: `usr_${String(i + 1).padStart(3, "0")}`,
  name: name!,
  email: email!,
  emailVerified: i % 5 !== 0,
  banned: email === "bob@spam.test",
  bannedReason: email === "bob@spam.test" ? "Abuse / spam signups" : null,
  bannedAt: email === "bob@spam.test" ? "2026-05-20T10:00:00Z" : null,
  createdAt: `2026-0${(i % 5) + 1}-1${i % 9}T09:00:00Z`,
}));

interface OrgSeed {
  id: string;
  name: string;
  slug: string;
  planId: string;
  interval: PlanInterval | null;
  base: number;
  members: string[];
}

const ORG_SEED: OrgSeed[] = [
  { id: "org_acme", name: "Acme Corp", slug: "acme-corp", planId: "growth", interval: "monthly", base: 84_200, members: ["usr_001", "usr_002"] },
  { id: "org_globex", name: "Globex", slug: "globex", planId: "growth_plus", interval: "annual", base: 910_500, members: ["usr_003", "usr_004"] },
  { id: "org_initech", name: "Initech", slug: "initech", planId: "free", interval: null, base: 7_320, members: ["usr_005"] },
  { id: "org_umbrella", name: "Umbrella Inc", slug: "umbrella", planId: "enterprise", interval: null, base: 5_120_000, members: ["usr_006", "usr_007"] },
  { id: "org_hooli", name: "Hooli", slug: "hooli", planId: "plan_hooli", interval: "quarterly", base: 1_450_000, members: ["usr_008", "usr_009"] },
  { id: "org_piedpiper", name: "Pied Piper", slug: "pied-piper", planId: "free", interval: null, base: 2_140, members: ["usr_010", "usr_011"] },
];

const ORGS: MockOrg[] = ORG_SEED.map((o, i) => ({
  id: o.id,
  name: o.name,
  slug: o.slug,
  status: "active" as OrgStatus,
  createdAt: `2026-0${(i % 5) + 1}-05T09:00:00Z`,
  planId: o.planId,
  billingInterval: o.interval,
  currentUsage: o.base,
  grantRequests: null,
  grantStart: null,
  grantKind: null,
  grantEnd: null,
  members: o.members.map((userId, mi) => ({
    userId,
    role: (mi === 0 ? "owner" : "member") as OrgRole,
    joinedAt: `2026-0${(i % 5) + 1}-06T09:00:00Z`,
  })),
}));

// --- derivations ------------------------------------------------------------

function grantOf(o: MockOrg): GrantInfo | null {
  if (!o.grantRequests || o.grantRequests <= 0 || !o.grantStart) return null;
  const period = toIsoDate().slice(0, 7);
  const kind: GrantKind = o.grantKind ?? "once";
  const endPeriod = kind === "once" ? null : o.grantEnd ?? o.grantStart;
  const active =
    kind === "once" ? period === o.grantStart : o.grantStart <= period && period <= (endPeriod ?? o.grantStart);
  return { requests: o.grantRequests, kind, startPeriod: o.grantStart, endPeriod, active };
}

interface PlanView {
  plan: OrgPlan;
  planId: string;
  planLabel: string;
  kind: PlanKind;
  baseMonthly: number | null;
  grant: GrantInfo | null;
  effective: number | null;
}

function planViewOf(o: MockOrg): PlanView {
  const p = planById(o.planId);
  const kind: PlanKind = p?.kind ?? "standard";
  const plan: OrgPlan = kind === "custom" ? "custom" : (o.planId as OrgPlan);
  const baseMonthly = p && !isUnlimited(p.monthlyRequests) ? (p.monthlyRequests as number) : null;
  const grant = grantOf(o);
  const effective = baseMonthly == null ? null : baseMonthly + (grant?.active ? grant.requests : 0);
  return { plan, planId: o.planId, planLabel: p?.name ?? o.planId, kind, baseMonthly, grant, effective };
}

function orgSummary(o: MockOrg): AdminOrgSummary {
  const v = planViewOf(o);
  return {
    id: o.id,
    name: o.name,
    slug: o.slug,
    plan: v.plan,
    planId: v.planId,
    planLabel: v.planLabel,
    status: o.status,
    memberCount: o.members.length,
    currentUsage: o.currentUsage,
    monthlyLimit: v.baseMonthly,
    effectiveMonthlyLimit: v.effective,
    createdAt: o.createdAt,
  };
}

const userById = (id: string) => USERS.find((u) => u.id === id);

function orgMembers(o: MockOrg): AdminMember[] {
  return o.members.map((m) => {
    const u = userById(m.userId);
    return { userId: m.userId, name: u?.name ?? null, email: u?.email ?? "unknown@unknown", role: m.role, banned: !!u?.banned, joinedAt: m.joinedAt };
  });
}

function recentUsageFor(seed: number): UsagePoint[] {
  const today = toIsoDate();
  const out: UsagePoint[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - i);
    const wave = 0.6 + 0.4 * Math.sin((i + seed) / 4);
    out.push({ bucket: d.toISOString().slice(0, 10), requests: Math.round((seed * 130 + 400) * wave) });
  }
  return out;
}

// --- AI settings (mock) — held in memory like the other admin mutations.
let mockAi: AiGlobalSettings = { enabled: true, message: "" };
const orgAiOverrides = new Map<string, boolean>();
const orgAiOf = (id: string): boolean => orgAiOverrides.get(id) ?? true;
const orgAuditOverrides = new Map<string, boolean | null>();

function orgDetail(o: MockOrg): AdminOrgDetail {
  const v = planViewOf(o);
  const paid = v.planId !== "free";
  const summary = orgSummary(o);
  return {
    ...summary,
    members: orgMembers(o),
    subscription: {
      plan: v.plan,
      planId: v.planId,
      planKind: v.kind,
      status: o.status,
      billingInterval: o.billingInterval,
      customLabel: v.kind === "custom" ? v.planLabel : null,
      currentPeriodEnd: paid ? "2026-07-01" : null,
      dodoCustomerId: paid ? `cus_mock_${o.id}` : null,
      dodoSubscriptionId: paid ? `sub_mock_${o.id}` : null,
      bonusRequests: o.grantRequests,
      bonusPeriod: o.grantStart,
      grantKind: o.grantKind,
      grantEndPeriod: o.grantEnd,
    },
    grant: v.grant,
    recentUsage: recentUsageFor(o.members.length + o.name.length),
    currentUsageAi: Math.round(summary.currentUsage * 0.1),
    aiEnabled: orgAiOf(o.id),
    auditLogsEnabled:
      orgAuditOverrides.get(o.id) ?? planHasAuditLogs({ id: v.planId, kind: v.kind }),
    auditLogsOverride: orgAuditOverrides.has(o.id) ? (orgAuditOverrides.get(o.id) ?? null) : null,
    auditRetentionDays: planAuditRetentionDays({ id: v.planId, kind: v.kind }),
  };
}

export function mockGetAiSettings(): AiGlobalSettings {
  return { ...mockAi };
}

export function mockSetAiSettings(s: AiGlobalSettings): AiGlobalSettings {
  mockAi = { enabled: s.enabled, message: s.message };
  return { ...mockAi };
}

export function mockSetOrgAi(id: string, enabled: boolean): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  orgAiOverrides.set(id, enabled);
  return orgDetail(o);
}

export function mockSetOrgAuditLogs(id: string, override: boolean | null): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  if (override === null) orgAuditOverrides.delete(id);
  else orgAuditOverrides.set(id, override);
  return orgDetail(o);
}

function planRank(p: OrgPlan): number {
  return { custom: 7, enterprise: 6, business: 5, growth_plus: 4, growth: 3, starter: 2, free: 1 }[p] ?? 0;
}

function userOrgs(id: string): AdminUserOrgRef[] {
  return ORGS.filter((o) => o.members.some((m) => m.userId === id)).map((o) => {
    const v = planViewOf(o);
    return {
      organizationId: o.id,
      organizationName: o.name,
      role: o.members.find((m) => m.userId === id)!.role,
      plan: v.plan,
      planId: v.planId,
    };
  });
}

function userSummary(u: MockUser): AdminUserSummary {
  const orgs = userOrgs(u.id);
  const topPlan = orgs.length ? orgs.map((o) => o.plan).sort((a, b) => planRank(b) - planRank(a))[0]! : null;
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    emailVerified: u.emailVerified,
    banned: u.banned,
    bannedReason: u.bannedReason,
    createdAt: u.createdAt,
    orgCount: orgs.length,
    topPlan,
  };
}

function userDetail(u: MockUser): AdminUserDetail {
  return { ...userSummary(u), bannedAt: u.bannedAt, organizations: userOrgs(u.id) };
}

function paginate<T>(items: T[], page = 1, pageSize = 25): Paginated<T> {
  const start = (page - 1) * pageSize;
  return { items: items.slice(start, start + pageSize), total: items.length, page, pageSize };
}

function multi(raw: string | null): string[] {
  return raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];
}

// --- mock API: data + orgs --------------------------------------------------

export function mockDataStatus(): DataStatus {
  return {
    rates: { latestDate: toIsoDate(), currencyCount: 31, totalRows: 12_412 },
    ppp: { years: { min: 1980, max: 2031 }, rows: 9207, projected: 1476, countries: 197 },
    counts: { organizations: ORGS.length, users: USERS.length, apiKeys: 14, bannedUsers: USERS.filter((u) => u.banned).length },
    mcp: { requestsThisPeriod: 3_204, connectedOrgs: 2 },
    ai: { requestsThisPeriod: 1_780 },
  };
}

export function mockListOrgs(q: URLSearchParams): Paginated<AdminOrgSummary> {
  let rows = ORGS.slice();
  const search = q.get("q")?.toLowerCase();
  const plan = multi(q.get("plan"));
  const status = multi(q.get("status"));
  if (search) rows = rows.filter((o) => `${o.name} ${o.id} ${o.slug}`.toLowerCase().includes(search));
  if (plan.length) rows = rows.filter((o) => plan.includes(planViewOf(o).plan));
  if (status.length) rows = rows.filter((o) => status.includes(o.status));
  const sort = q.get("sort") ?? "createdAt";
  const dir = q.get("order") === "asc" ? 1 : -1;
  rows.sort((a, b) => dir * cmpOrg(a, b, sort));
  return paginate(rows.map(orgSummary), Number(q.get("page") ?? 1), Number(q.get("pageSize") ?? 25));
}

function cmpOrg(a: MockOrg, b: MockOrg, sort: string): number {
  switch (sort) {
    case "name":
      return a.name.localeCompare(b.name);
    case "plan":
      return planRank(planViewOf(a).plan) - planRank(planViewOf(b).plan);
    case "usage":
      return a.currentUsage - b.currentUsage;
    case "members":
      return a.members.length - b.members.length;
    default:
      return a.createdAt.localeCompare(b.createdAt);
  }
}

export function mockGetOrg(id: string): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  return orgDetail(o);
}

export function mockSetOrgPlan(id: string, body: SetPlanRequest): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  const plan = planById(body.planId);
  if (!plan) throw new Error(`Plan not found: ${body.planId}`);
  const hasPrices = Object.values(plan.prices).some((v) => (v ?? 0) > 0);
  const samePlan = o.planId === body.planId;
  if (samePlan && o.billingInterval && body.billingInterval && body.billingInterval !== o.billingInterval) {
    throw new Error("Billing interval is locked once assigned. Assign a different plan to change it.");
  }
  if (!samePlan || !o.billingInterval) {
    if (hasPrices && !body.billingInterval) throw new Error("billingInterval is required for a paid plan.");
    o.billingInterval = body.billingInterval ?? (samePlan ? o.billingInterval : null);
  }
  o.planId = body.planId;
  if (body.status) o.status = body.status;
  if (!PLAN_ORGS.some((x) => x.planId === body.planId && x.orgId === id)) {
    PLAN_ORGS.push({ planId: body.planId, orgId: id });
  }
  return orgDetail(o);
}

export function mockSetMemberRole(id: string, uid: string, role: OrgRole): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  const m = o.members.find((x) => x.userId === uid);
  if (!m) throw new Error("Member not found");
  if (m.role === "owner" && role !== "owner") {
    throw new Error("An owner's role can't be changed directly. Transfer ownership by promoting another member to owner.");
  }
  if (role === "owner") o.members.forEach((x) => { if (x.role === "owner") x.role = "admin"; });
  m.role = role;
  return orgDetail(o);
}

export function mockGrant(id: string, body: GrantRequest): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  const add = Math.max(0, Math.floor(Number(body.addRequests)));
  const start = toIsoDate().slice(0, 7);
  if (add <= 0) {
    o.grantRequests = o.grantStart = o.grantKind = o.grantEnd = null;
    return orgDetail(o);
  }
  const kind: GrantKind = body.kind === "until" || body.kind === "months" ? body.kind : "once";
  let end: string | null = null;
  if (kind === "until") end = body.untilPeriod ?? start;
  else if (kind === "months") end = addMonths(start, Math.max(1, Math.floor(Number(body.months))) - 1);
  o.grantRequests = add;
  o.grantStart = start;
  o.grantKind = kind;
  o.grantEnd = end;
  return orgDetail(o);
}

export function mockSetOrgCap(
  id: string,
  body: { adminRequests?: number | null; orgRequests?: number | null },
): AdminOrgDetail {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  const adminCap = body.adminRequests == null ? null : Math.max(0, Math.floor(Number(body.adminRequests)));
  let orgCap = body.orgRequests == null ? null : Math.max(0, Math.floor(Number(body.orgRequests)));
  if (orgCap != null && adminCap != null && orgCap > adminCap) orgCap = adminCap; // clamp to ceiling
  return { ...orgDetail(o), adminHardCapRequests: adminCap, hardCapRequests: orgCap };
}

// Refund state the mock has to remember across calls (no real webhook).
const REFUND_STATUS: Record<string, InvoiceStatus> = {}; // invoiceId → overridden status
const REFUND_ROWS: Record<string, InvoiceDTO[]> = {}; // orgId → extra refund ledger rows

export function mockRefund(id: string, body: RefundRequest): BillingSummary {
  const billing = mockBilling(id);
  const target = billing.invoices.find((x) => x.id === body.invoiceId);
  if (!target) throw new Error("Invoice not found for this organization.");
  if (target.status !== "paid") throw new Error(`Only paid invoices can be refunded (this one is "${target.status}").`);
  // Simulate Dodo accepting the refund — original goes pending until "settled".
  REFUND_STATUS[target.id] = "refund_pending";
  const rows = REFUND_ROWS[id] ?? (REFUND_ROWS[id] = []);
  const refundId = `${target.id}.rf`;
  if (!rows.some((r) => r.id === refundId)) {
    rows.push({ id: refundId, date: toIsoDate(), amountCents: target.amountCents, currency: target.currency, status: "refund_pending", description: "Refund", hostedUrl: null, refundable: false });
  }
  return mockBilling(id);
}

export function mockBilling(id: string): BillingSummary {
  const o = ORGS.find((x) => x.id === id);
  if (!o) throw new Error(`Org not found: ${id}`);
  const v = planViewOf(o);
  const plan = planById(o.planId);
  const cents = o.billingInterval ? plan?.prices[o.billingInterval] ?? 0 : plan?.prices.monthly ?? 0;
  const invoices: InvoiceDTO[] = [];
  if (cents > 0) {
    const today = toIsoDate();
    for (let i = 0; i < 6; i++) {
      const d = new Date(`${today}T00:00:00Z`);
      d.setUTCMonth(d.getUTCMonth() - i);
      const invId = `inv_${o.id}_${i}`;
      const status: InvoiceStatus = REFUND_STATUS[invId] ?? (i === 0 ? "open" : "paid");
      invoices.push({ id: invId, date: d.toISOString().slice(0, 10), amountCents: cents, currency: "USD", status, description: `${v.planLabel}, ${o.billingInterval ?? "monthly"} subscription`, hostedUrl: "#", refundable: status === "paid" && cents > 0 });
    }
  }
  invoices.push(...(REFUND_ROWS[id] ?? []));
  // Billing history is always newest-first by date.
  invoices.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  const totalPaidCents = invoices.filter((x) => x.status === "paid").reduce((s, x) => s + x.amountCents, 0);
  const paid = o.planId !== "free";
  return {
    organizationId: id,
    plan: v.plan,
    planLabel: v.planLabel,
    status: o.status,
    currency: "USD",
    totalPaidCents,
    invoiceCount: invoices.length,
    currentPeriodEnd: paid ? "2026-07-01" : null,
    dodoCustomerId: paid ? `cus_mock_${o.id}` : null,
    dodoSubscriptionId: paid ? `sub_mock_${o.id}` : null,
    invoices,
  };
}

// --- mock API: plans --------------------------------------------------------

function planUsage(p: PlanRecord): PlanUsageInfo {
  return { ...p, orgCount: ORGS.filter((o) => o.planId === p.id).length };
}

// plan → orgs availability list (mirrors the plan_orgs table; seeded from active plans).
const PLAN_ORGS: { planId: string; orgId: string }[] = ORGS.map((o) => ({ planId: o.planId, orgId: o.id }));

function planOrgRefs(planId: string): PlanOrgRef[] {
  return PLAN_ORGS.filter((x) => x.planId === planId)
    .map((x) => ORGS.find((o) => o.id === x.orgId))
    .filter((o): o is MockOrg => !!o)
    .map((o) => ({ id: o.id, name: o.name, slug: o.slug, active: o.planId === planId }))
    .sort((a, b) => (a.active === b.active ? a.name.localeCompare(b.name) : a.active ? -1 : 1));
}

function planDetail(p: PlanRecord): PlanDetail {
  return { ...planUsage(p), orgs: planOrgRefs(p.id) };
}

export function mockListPlans(): PlanUsageInfo[] {
  return PLANS.map(planUsage);
}
/** Plans assignable to one org: standard + that org's allow-listed custom plans. */
export function mockListOrgPlans(orgId: string): PlanUsageInfo[] {
  return PLANS.filter(
    (p) => p.kind === "standard" || PLAN_ORGS.some((x) => x.planId === p.id && x.orgId === orgId),
  ).map(planUsage);
}
export function mockGetPlan(id: string): PlanDetail {
  const p = planById(id);
  if (!p) throw new Error(`Plan not found: ${id}`);
  return planDetail(p);
}
export function mockAddPlanOrg(planId: string, orgId: string): PlanDetail {
  const p = planById(planId);
  if (!p) throw new Error(`Plan not found: ${planId}`);
  if (!ORGS.find((o) => o.id === orgId)) throw new Error(`Org not found: ${orgId}`);
  if (!PLAN_ORGS.some((x) => x.planId === planId && x.orgId === orgId)) PLAN_ORGS.push({ planId, orgId });
  return planDetail(p);
}
export function mockRemovePlanOrg(planId: string, orgId: string): PlanDetail {
  const p = planById(planId);
  if (!p) throw new Error(`Plan not found: ${planId}`);
  const org = ORGS.find((o) => o.id === orgId);
  if (org && org.planId === planId) {
    throw new Error("This plan is the organization's active plan. It can't be removed from the list.");
  }
  const idx = PLAN_ORGS.findIndex((x) => x.planId === planId && x.orgId === orgId);
  if (idx >= 0) PLAN_ORGS.splice(idx, 1);
  return planDetail(p);
}
export function mockCreatePlan(body: PlanWriteRequest): PlanRecord {
  const id = `plan_${Math.random().toString(36).slice(2, 10)}`;
  const p = mkPlan(
    id,
    body.name.trim() || "Untitled plan",
    "custom",
    body.monthlyRequests,
    body.rateLimit,
    body.prices ?? {},
    body.overageCents ?? null,
    body.overageUnit ?? null,
    body.dodoProductIds ?? {},
  );
  p.archived = !!body.archived;
  if (body.historyApiEnabled != null) p.historyApiEnabled = body.historyApiEnabled;
  PLANS.push(p);
  return p;
}
export function mockCopyPlan(id: string): PlanRecord {
  const src = planById(id);
  if (!src) throw new Error(`Plan not found: ${id}`);
  const nid = `plan_${Math.random().toString(36).slice(2, 10)}`;
  const p = mkPlan(nid, `Copy of ${src.name}`, "custom", src.monthlyRequests, src.rateLimit, { ...src.prices }, src.overageCents, src.overageUnit, { ...src.dodoProductIds });
  PLANS.push(p);
  return p;
}
export function mockUpdatePlan(id: string, body: PlanWriteRequest): PlanRecord {
  const p = planById(id);
  if (!p) throw new Error(`Plan not found: ${id}`);
  p.name = body.name.trim() || p.name;
  p.monthlyRequests = body.monthlyRequests;
  p.rateLimit = body.rateLimit;
  p.prices = body.prices ?? {};
  p.overageCents = body.overageCents ?? null;
  p.overageUnit = body.overageUnit ?? null;
  p.dodoProductIds = body.dodoProductIds ?? {};
  p.archived = !!body.archived;
  p.historyApiEnabled = body.historyApiEnabled ?? p.historyApiEnabled;
  p.updatedAt = new Date().toISOString();
  return p;
}
export function mockDeletePlan(id: string): { ok: boolean } {
  const p = planById(id);
  if (!p) throw new Error(`Plan not found: ${id}`);
  if (p.kind === "standard") throw new Error("Default plans can't be deleted.");
  const count = ORGS.filter((o) => o.planId === id).length;
  if (count > 0) {
    const err = new Error(`Plan is in use by ${count} organization(s).`) as Error & { orgCount?: number };
    err.orgCount = count;
    throw err;
  }
  const idx = PLANS.findIndex((x) => x.id === id);
  if (idx >= 0) PLANS.splice(idx, 1);
  for (let i = PLAN_ORGS.length - 1; i >= 0; i--) {
    if (PLAN_ORGS[i]!.planId === id) PLAN_ORGS.splice(i, 1);
  }
  return { ok: true };
}

// --- mock API: AI credit packages -------------------------------------------

const AI_CREDIT_PKGS: AiCreditPackageRecord[] = AI_CREDIT_PACKAGES.map((p, i) => ({
  id: p.id,
  credits: p.credits,
  priceCents: p.priceCents,
  dodoProductId: null,
  active: true,
  sort: i + 1,
}));

export function mockListAiCreditPackages(): AiCreditPackageRecord[] {
  return AI_CREDIT_PKGS.slice().sort((a, b) => a.sort - b.sort || a.credits - b.credits);
}
export function mockUpdateAiCreditPackage(
  id: string,
  body: AiCreditPackageWrite,
): AiCreditPackageRecord {
  let pkg = AI_CREDIT_PKGS.find((p) => p.id === id);
  if (!pkg) {
    pkg = { id, credits: 0, priceCents: 0, dodoProductId: null, active: true, sort: 99 };
    AI_CREDIT_PKGS.push(pkg);
  }
  pkg.credits = Math.max(1, Math.floor(Number(body.credits) || pkg.credits));
  pkg.priceCents = Math.max(0, Math.floor(Number(body.priceCents) || 0));
  pkg.dodoProductId = body.dodoProductId?.trim() || null;
  pkg.active = body.active !== false;
  if (body.sort != null) pkg.sort = Math.floor(Number(body.sort));
  return pkg;
}

// --- mock API: users --------------------------------------------------------

export function mockListUsers(q: URLSearchParams): Paginated<AdminUserSummary> {
  let rows = USERS.slice();
  const search = q.get("q")?.toLowerCase();
  const banned = multi(q.get("banned"));
  const plan = multi(q.get("plan"));
  if (search) rows = rows.filter((u) => `${u.name} ${u.email} ${u.id}`.toLowerCase().includes(search));
  if (banned.length === 1) rows = rows.filter((u) => u.banned === (banned[0] === "true"));
  if (plan.length) rows = rows.filter((u) => userOrgs(u.id).some((o) => plan.includes(o.plan)));
  const sort = q.get("sort") ?? "createdAt";
  const dir = q.get("order") === "asc" ? 1 : -1;
  rows.sort((a, b) => dir * cmpUser(a, b, sort));
  return paginate(rows.map(userSummary), Number(q.get("page") ?? 1), Number(q.get("pageSize") ?? 25));
}

function cmpUser(a: MockUser, b: MockUser, sort: string): number {
  switch (sort) {
    case "name":
      return a.name.localeCompare(b.name);
    case "email":
      return a.email.localeCompare(b.email);
    case "orgs":
      return userOrgs(a.id).length - userOrgs(b.id).length;
    default:
      return a.createdAt.localeCompare(b.createdAt);
  }
}

export function mockGetUser(id: string): AdminUserDetail {
  const u = userById(id);
  if (!u) throw new Error(`User not found: ${id}`);
  return userDetail(u);
}
export function mockBanUser(id: string, reason: string | null): AdminUserDetail {
  const u = userById(id);
  if (!u) throw new Error(`User not found: ${id}`);
  u.banned = true;
  u.bannedReason = reason;
  u.bannedAt = new Date().toISOString();
  return userDetail(u);
}
export function mockUnbanUser(id: string): AdminUserDetail {
  const u = userById(id);
  if (!u) throw new Error(`User not found: ${id}`);
  u.banned = false;
  u.bannedReason = null;
  u.bannedAt = null;
  return userDetail(u);
}
export function mockDeleteUser(id: string): { ok: boolean; deletedOrgs: { id: string; name: string }[] } {
  const blocking = ORGS.filter((o) => o.members.length > 1 && o.members.some((m) => m.userId === id && m.role === "owner"));
  if (blocking.length) {
    const err = new Error("User solely owns organizations with other members. Transfer ownership or delete those orgs first.") as Error & { organizations?: { id: string; name: string }[] };
    err.organizations = blocking.map((o) => ({ id: o.id, name: o.name }));
    throw err;
  }
  const solo = ORGS.filter((o) => o.members.length <= 1 && o.members.some((m) => m.userId === id && m.role === "owner"));
  const soloIds = new Set(solo.map((o) => o.id));
  for (let i = ORGS.length - 1; i >= 0; i--) {
    if (soloIds.has(ORGS[i]!.id)) ORGS.splice(i, 1);
    else ORGS[i]!.members = ORGS[i]!.members.filter((m) => m.userId !== id);
  }
  const idx = USERS.findIndex((u) => u.id === id);
  if (idx >= 0) USERS.splice(idx, 1);
  return { ok: true, deletedOrgs: solo.map((o) => ({ id: o.id, name: o.name })) };
}

// --- mock API: usage analytics ----------------------------------------------

export function mockUsage(q: URLSearchParams): UsageAnalytics {
  const today = toIsoDate();
  const to = q.get("to") || today;
  const from = q.get("from") || addDays(to, -29);
  const granularity = (q.get("granularity") as UsageGranularity) || "day";
  const orgFilter = q.get("org");
  const planFilter = q.get("plan");
  const pool = ORGS.filter((o) => (!orgFilter || o.id === orgFilter) && (!planFilter || planViewOf(o).plan === planFilter));

  const days = Math.max(1, daysBetween(from, to) + 1);
  const overTime: UsagePoint[] = [];
  const planBuckets = new Map<OrgPlan, number[]>();
  for (let i = 0; i < days; i++) {
    let dayTotal = 0;
    for (const o of pool) {
      const plan = planViewOf(o).plan;
      const wave = 0.55 + 0.45 * Math.sin((i + o.name.length) / 5);
      const v = Math.round((o.currentUsage / 30) * wave);
      dayTotal += v;
      if (!planBuckets.has(plan)) planBuckets.set(plan, new Array(days).fill(0));
      planBuckets.get(plan)![i]! += v;
    }
    overTime.push({ bucket: bucketLabel(addDays(from, i), granularity), requests: dayTotal });
  }
  const merged = mergeBuckets(overTime);
  const byPlan: NamedSeries[] = [...planBuckets.entries()].map(([plan, arr]) => ({
    key: plan,
    label: TIER_LABEL[plan],
    points: mergeBuckets(arr.map((requests, i) => ({ bucket: bucketLabel(addDays(from, i), granularity), requests }))),
  }));
  const topOrgs = pool
    .map((o) => ({ organizationId: o.id, name: o.name, requests: o.currentUsage }))
    .sort((a, b) => b.requests - a.requests)
    .slice(0, 10);
  return {
    range: { from, to, granularity },
    totals: {
      requests: merged.reduce((s, p) => s + p.requests, 0),
      activeOrgs: pool.length,
      aiRequests: Math.round(merged.reduce((s, p) => s + p.requests, 0) * 0.1),
      growthPct: 12.4,
    },
    overTime: merged,
    byPlan,
    topOrgs,
  };
}

export function mockSyncRates() {
  const today = toIsoDate();
  return { ok: true, dates: [today], latestDate: today, currencyCount: 31, rowsWritten: 32, written: [today], skipped: [] };
}
export function mockFetchRatesForDate(date: string) {
  // Pretend even-day-of-month dates are already present, to exercise the skip path.
  const exists = parseInt(date.slice(-2), 10) % 2 === 0;
  return exists
    ? { ok: true, dates: [date], latestDate: toIsoDate(), currencyCount: 31, rowsWritten: 0, written: [], skipped: [date] }
    : { ok: true, dates: [date], latestDate: toIsoDate(), currencyCount: 31, rowsWritten: 32, written: [date], skipped: [] };
}
export function mockSyncPpp() {
  return { ok: true, fetched: 9207, eligible: 9100, changed: 0, unchanged: 9100, years: { min: 1980, max: 2031 } };
}
export function mockUploadPpp() {
  return { ok: true, fetched: 9207, eligible: 9100, changed: 142, unchanged: 8958, years: { min: 1980, max: 2031 } };
}
export function mockUploadRates(payload: unknown) {
  const entries = Array.isArray((payload as { dates?: unknown[] })?.dates)
    ? (payload as { dates: { date?: string }[] }).dates
    : [];
  const dates = entries.map((d) => d?.date).filter((d): d is string => typeof d === "string");
  const latestDate = dates.length ? dates[dates.length - 1]! : toIsoDate();
  return { ok: true, dates, latestDate, currencyCount: 31, rowsWritten: dates.length * 32, written: dates, skipped: [] };
}

// --- date helpers -----------------------------------------------------------

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function addMonths(period: string, months: number): string {
  const [y, m] = period.split("-").map((n) => parseInt(n, 10));
  const d = new Date(Date.UTC(y!, m! - 1 + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function daysBetween(from: string, to: string): number {
  return Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000);
}
function bucketLabel(date: string, g: UsageGranularity): string {
  if (g === "month") return date.slice(0, 7);
  if (g === "week") {
    const d = new Date(`${date}T00:00:00Z`);
    const onejan = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d.getTime() - onejan.getTime()) / 86_400_000 + onejan.getUTCDay() + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
  }
  return date;
}
function mergeBuckets(points: UsagePoint[]): UsagePoint[] {
  const map = new Map<string, number>();
  for (const p of points) map.set(p.bucket, (map.get(p.bucket) ?? 0) + p.requests);
  return [...map.entries()].map(([bucket, requests]) => ({ bucket, requests }));
}

// --- Status / incidents (in-memory) ----------------------------------------

let MOCK_INCIDENTS: IncidentDTO[] = [
  {
    id: "inc_seed_1",
    title: "Elevated conversion latency",
    impact: "minor",
    status: "resolved",
    auto: false,
    startedAt: "2026-05-20T09:12:00.000Z",
    resolvedAt: "2026-05-20T10:05:00.000Z",
    updates: [
      { status: "investigating", body: "Elevated latency on /convert; investigating.", createdAt: "2026-05-20T09:12:00.000Z" },
      { status: "monitoring", body: "A fix is deployed; monitoring recovery.", createdAt: "2026-05-20T09:48:00.000Z" },
      { status: "resolved", body: "Latency is back to normal.", createdAt: "2026-05-20T10:05:00.000Z" },
    ],
  },
];

function mockUptime(): { pct90: number; days: UptimeDay[] } {
  const down = new Set([23, 61]);
  const warn = new Set([12, 44, 78]);
  const base = new Date();
  const days: UptimeDay[] = [];
  let up = 0;
  let total = 0;
  for (let i = 89; i >= 0; i--) {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() - i);
    const idx = 89 - i;
    const status = down.has(idx) ? "outage" : warn.has(idx) ? "degraded" : "operational";
    const uptimePct = status === "outage" ? 97.2 : status === "degraded" ? 99.6 : 100;
    up += uptimePct;
    total += 100;
    days.push({ date: d.toISOString().slice(0, 10), status, uptimePct });
  }
  return { pct90: Math.round((up / total) * 10000) / 100, days };
}

export function mockStatusSummary(): StatusResponse {
  const active = MOCK_INCIDENTS.filter((i) => i.resolvedAt == null);
  const status = active.some((i) => i.impact !== "minor")
    ? "outage"
    : active.length
      ? "degraded"
      : "operational";
  return { status, updatedAt: new Date().toISOString(), uptime: mockUptime(), incidents: MOCK_INCIDENTS };
}

export function mockListIncidents(): IncidentDTO[] {
  return MOCK_INCIDENTS;
}

export function mockCreateIncident(body: {
  title: string;
  impact: IncidentImpact;
  status: IncidentStatus;
  body: string;
}): { ok: boolean; id: string } {
  const id = `inc_${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();
  MOCK_INCIDENTS = [
    {
      id,
      title: body.title,
      impact: body.impact,
      status: body.status,
      auto: false,
      startedAt: now,
      resolvedAt: body.status === "resolved" ? now : null,
      updates: [{ status: body.status, body: body.body, createdAt: now }],
    },
    ...MOCK_INCIDENTS,
  ];
  return { ok: true, id };
}

export function mockPostIncidentUpdate(
  id: string,
  body: { status: IncidentStatus; body: string },
): { ok: boolean } {
  const now = new Date().toISOString();
  MOCK_INCIDENTS = MOCK_INCIDENTS.map((i) =>
    i.id === id
      ? {
          ...i,
          status: body.status,
          resolvedAt: body.status === "resolved" ? now : null,
          updates: [...i.updates, { status: body.status, body: body.body, createdAt: now }],
        }
      : i,
  );
  return { ok: true };
}

export function mockDeleteIncident(id: string): { ok: boolean } {
  MOCK_INCIDENTS = MOCK_INCIDENTS.filter((i) => i.id !== id);
  return { ok: true };
}
